import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  Popconfirm,
  Progress,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { NotificationOutlined, ShopOutlined, TeamOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useBatches } from '../hooks/useBatches';
import { useImChannel } from '../hooks/useImChannel';
import {
  adminAlertStyle,
  adminContentCardStyle,
  adminFilterCardStyle,
  adminPageWrap,
} from '../styles/adminPageShell';

const { Text } = Typography;

const STORE_FILTER_ALL = 'all';

type DoneFilter = 'all' | 'qualityOpen' | 'competencyOpen' | 'anyOpen' | 'applicableDone';
type StoreFilter = typeof STORE_FILTER_ALL | string;

interface StoreSummary {
  storeKey: string;
  storeLabel: string;
  unresolved: boolean;
  headcount: number;
  qualityApplicableCount: number;
  qualityDoneCount: number;
  competencyHpApplicableCount: number;
  competencyHpDoneCount: number;
  bothDoneCount: number;
  qualityPct: number;
  competencyHpPct: number;
}

interface StoreProgressSummaryResp {
  batchId: number;
  storeKeyRule: string;
  stores: StoreSummary[];
}

interface PersonRow {
  evaluatorId: number;
  evaluateeId: number;
  name: string;
  deptPath: string | null;
  storeKey: string;
  unresolved: boolean;
  leftRoster: boolean;
  quality: { total: number; done: number; pending: number; status: string };
  competency: {
    total: number;
    done: number;
    pending: number;
    status: string;
    evaluable: boolean;
  };
  competencyHpCombined: string;
  bothDone: boolean;
}

function shortDept(path: string | null | undefined) {
  if (!path) return '-';
  const parts = path.split(/[\/\\>›→]/).map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 2) return parts.join(' / ');
  return parts.slice(-2).join(' / ');
}

function qualityTag(status: string) {
  if (status === 'DONE') return <Tag color="success">已齐</Tag>;
  if (status === 'PARTIAL') return <Tag color="processing">部分提交</Tag>;
  if (status === 'NONE') return <Tag color="warning">未提交</Tag>;
  return <Tag>不适用</Tag>;
}

function combinedTag(status: string) {
  if (status === 'DONE') return <Tag color="success">已交</Tag>;
  if (status === 'PENDING') return <Tag color="warning">未交</Tag>;
  return <Tag>不适用</Tag>;
}

function personStoreLabel(storeKey: string, unresolved: boolean) {
  if (unresolved || storeKey === '__unresolved__') return '未归门店';
  return storeKey;
}

function pct(done: number, total: number) {
  if (total <= 0) return 0;
  return Math.round((done / total) * 100);
}

/** 门店完成度口径下仍有未完成项的评价人（可催办） */
function isOpenPerson(r: PersonRow) {
  return !r.bothDone && !r.leftRoster;
}

interface RemindResult {
  pendingEvaluators: number;
  sentCount: number;
  skippedNoDing: number;
  skippedNoPending: number;
  sentNames: string[];
}

/** 全部门店汇总：与单店摘要同款样式 */
function aggregateAllStores(stores: StoreSummary[]): StoreSummary {
  const headcount = stores.reduce((n, s) => n + s.headcount, 0);
  const qualityDoneCount = stores.reduce((n, s) => n + s.qualityDoneCount, 0);
  const qualityApplicableCount = stores.reduce((n, s) => n + s.qualityApplicableCount, 0);
  const competencyHpDoneCount = stores.reduce((n, s) => n + s.competencyHpDoneCount, 0);
  const competencyHpApplicableCount = stores.reduce(
    (n, s) => n + s.competencyHpApplicableCount,
    0,
  );
  const bothDoneCount = stores.reduce((n, s) => n + s.bothDoneCount, 0);
  return {
    storeKey: STORE_FILTER_ALL,
    storeLabel: '全部评价人',
    unresolved: false,
    headcount,
    qualityDoneCount,
    qualityApplicableCount,
    competencyHpDoneCount,
    competencyHpApplicableCount,
    bothDoneCount,
    qualityPct: pct(qualityDoneCount, qualityApplicableCount),
    competencyHpPct: pct(competencyHpDoneCount, competencyHpApplicableCount),
  };
}

function SingleStoreBoard({ store }: { store: StoreSummary }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(140px, 1.2fr) repeat(3, minmax(0, 1fr))',
        gap: 12,
        alignItems: 'center',
        maxHeight: 100,
        padding: '4px 0',
      }}
    >
      <div>
        <Space size={6}>
          <ShopOutlined style={{ color: store.unresolved ? '#fa8c16' : '#722ed1' }} />
          <span style={{ fontWeight: 700, fontSize: 15 }}>{store.storeLabel}</span>
          {store.unresolved ? <Tag color="orange">未归店</Tag> : null}
        </Space>
        <div style={{ marginTop: 4, color: '#888', fontSize: 12 }}>人数 {store.headcount}</div>
      </div>
      <div>
        <div style={{ fontSize: 12, color: '#888' }}>素质已齐</div>
        <div style={{ fontWeight: 600, fontSize: 16 }}>
          {store.qualityDoneCount}/{store.qualityApplicableCount}
        </div>
        <Progress percent={store.qualityPct} size="small" showInfo={false} strokeColor="#1677ff" />
      </div>
      <div>
        <div style={{ fontSize: 12, color: '#888' }}>胜任力已交</div>
        <div style={{ fontWeight: 600, fontSize: 16 }}>
          {store.competencyHpDoneCount}/{store.competencyHpApplicableCount}
        </div>
        <Progress percent={store.competencyHpPct} size="small" showInfo={false} strokeColor="#722ed1" />
      </div>
      <div>
        <div style={{ fontSize: 12, color: '#888' }}>已全部完成</div>
        <div style={{ fontWeight: 600, fontSize: 16 }}>{store.bothDoneCount}</div>
        <Text type="secondary" style={{ fontSize: 11 }}>
          素质 {store.qualityPct}% · 胜任力 {store.competencyHpPct}%
        </Text>
      </div>
    </div>
  );
}

export function StoreProgressPage() {
  const { batches, batchId, setBatchId, isLoading: loadingBatches } = useBatches();
  const { channel, label: imLabel } = useImChannel();
  const [doneFilter, setDoneFilter] = useState<DoneFilter>('all');
  const [keyword, setKeyword] = useState('');
  const [storeFilter, setStoreFilter] = useState<StoreFilter>(STORE_FILTER_ALL);
  const [remindingId, setRemindingId] = useState<number | null>(null);

  const selectedStoreKey = storeFilter === STORE_FILTER_ALL ? null : storeFilter;
  const viewingAllStores = storeFilter === STORE_FILTER_ALL;
  const canPushRemind = channel !== 'wecom';

  const { data, isLoading } = useQuery<StoreProgressSummaryResp>({
    queryKey: ['store-progress', batchId],
    queryFn: () => api.get('/admin/store-progress', { params: { batchId } }),
    enabled: !!batchId,
  });

  const { data: peopleResp, isLoading: loadingPeople } = useQuery<{
    people: PersonRow[];
    storeLabel: string;
  }>({
    queryKey: ['store-progress-people', batchId, storeFilter],
    queryFn: () =>
      api.get('/admin/store-progress/people', {
        params: { batchId, storeKey: storeFilter },
      }),
    enabled: !!batchId,
  });

  // 「未归门店」桶（总经理等无二级部门 / 门店识别不出）必须留着：过滤掉会让这些人
  // 在明细里看不见也催办不到，全员未完成人数因此少算。
  const stores = useMemo(() => data?.stores ?? [], [data?.stores]);
  const activeStore = stores.find((s) => s.storeKey === selectedStoreKey) ?? null;
  const boardStore = useMemo(
    () => (viewingAllStores ? aggregateAllStores(stores) : activeStore),
    [viewingAllStores, stores, activeStore],
  );
  const peopleFilterEnabled = !!batchId;

  const filteredPeople = useMemo(() => {
    const rows = peopleResp?.people ?? [];
    const kw = keyword.trim();
    return rows.filter((r) => {
      if (kw && !r.name.includes(kw) && !(r.deptPath || '').includes(kw)) return false;
      if (doneFilter === 'qualityOpen' && !(r.quality.status === 'PARTIAL' || r.quality.status === 'NONE')) {
        return false;
      }
      if (doneFilter === 'competencyOpen' && r.competencyHpCombined !== 'PENDING') return false;
      if (doneFilter === 'anyOpen') {
        const qualityOpen = r.quality.status === 'PARTIAL' || r.quality.status === 'NONE';
        const competencyOpen = r.competencyHpCombined === 'PENDING';
        if (!qualityOpen && !competencyOpen) return false;
      }
      if (doneFilter === 'applicableDone' && !r.bothDone) return false;
      return true;
    });
  }, [peopleResp, keyword, doneFilter]);

  const storeOptions = useMemo(
    () => [
      { value: STORE_FILTER_ALL, label: '全部门店' },
      ...stores.map((s) => ({
        value: s.storeKey,
        label: s.storeLabel,
      })),
    ],
    [stores],
  );

  const openInView = useMemo(() => filteredPeople.filter(isOpenPerson), [filteredPeople]);

  const remindMutation = useMutation({
    mutationFn: (evaluatorIds: number[]) =>
      api.post<any, RemindResult>('/admin/store-progress/remind', {
        batchId,
        evaluatorIds,
      }),
    onSuccess: (res) => {
      const extra: string[] = [];
      if (res.skippedNoDing > 0) extra.push(`${res.skippedNoDing} 人未绑钉钉跳过`);
      if (res.skippedNoPending > 0) extra.push(`${res.skippedNoPending} 人已无待办跳过`);
      if (res.sentCount === 0) {
        message.warning(
          extra.length ? `未推送任何人（${extra.join('，')}）` : '没有可推送的未完成评价人',
        );
        return;
      }
      message.success(
        `已推送 ${imLabel} 催办 ${res.sentCount} 人${extra.length ? `（${extra.join('，')}）` : ''}`,
      );
    },
    onError: (err: any) => {
      message.error(err?.response?.data?.message ?? err?.message ?? '催办推送失败');
    },
    onSettled: () => setRemindingId(null),
  });

  const remindOne = (evaluatorId: number) => {
    setRemindingId(evaluatorId);
    remindMutation.mutate([evaluatorId]);
  };

  const remindOpenInView = () => {
    const ids = openInView.map((r) => r.evaluatorId);
    if (ids.length === 0) {
      message.info('当前列表没有可催办的未完成评价人');
      return;
    }
    remindMutation.mutate(ids);
  };

  return (
    <div style={adminPageWrap}>
      <div className="tg-page-title">门店完成度</div>

      <Alert
        type="info"
        showIcon
        style={{ ...adminAlertStyle, marginBottom: 12 }}
        message="按门店查看评价人的素质项 + 胜任力/高潜作答完成情况"
        description={
          <>
            一行一名评价人。素质「已齐」= 其名下评价任务全部已提交（锁定未提交仍算未完成）。胜任力「已交」=
            其名下每位直属下属的胜任力与高潜均已提交（从未打开表单也算待交）。门店按评价人所在店分桶；总经理等不属于任何门店的人归入「未归门店」，同样计入未完成与催办。
            {canPushRemind
              ? ` 可对未完成评价人直接推送${imLabel}催办。`
              : ' 企微通知暂未开通，暂不支持页内催办。'}
          </>
        }
      />

      <Card style={{ ...adminFilterCardStyle, marginBottom: 12 }} styles={{ body: { paddingBottom: 12 } }}>
        <Space wrap size={12}>
          <span>批次：</span>
          <Select
            placeholder="选择批次"
            style={{ width: 200 }}
            options={batches.map((b) => ({ label: b.name, value: b.id }))}
            value={batchId}
            onChange={(v) => {
              setBatchId(v);
              setStoreFilter(STORE_FILTER_ALL);
              setDoneFilter('all');
              setKeyword('');
            }}
            loading={loadingBatches}
          />
          <span style={{ marginLeft: 4 }}>门店：</span>
          <Select
            style={{ width: 220 }}
            value={storeFilter}
            onChange={setStoreFilter}
            options={storeOptions}
            disabled={!batchId || isLoading || stores.length === 0}
            showSearch
            optionFilterProp="label"
          />
          <span style={{ marginLeft: 4 }}>完成情况：</span>
          <Select
            style={{ width: 150 }}
            value={doneFilter}
            onChange={setDoneFilter}
            disabled={!peopleFilterEnabled}
            options={[
              { value: 'all', label: '全部' },
              { value: 'qualityOpen', label: '素质未齐' },
              { value: 'competencyOpen', label: '胜任力未交' },
              { value: 'anyOpen', label: '未全部完成' },
              { value: 'applicableDone', label: '已全部完成' },
            ]}
          />
          <Input.Search
            placeholder="筛选评价人姓名 / 部门"
            allowClear
            style={{ width: 220 }}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            disabled={!peopleFilterEnabled}
          />
        </Space>
      </Card>

      <Card
        style={{ ...adminContentCardStyle, marginBottom: 12 }}
        styles={{ body: { paddingTop: 10, paddingBottom: 10, maxHeight: 120, overflow: 'hidden' } }}
        title={
          <Space size={6}>
            <ShopOutlined />
            <span style={{ fontSize: 14 }}>{viewingAllStores ? '全部评价人进度' : '店级摘要'}</span>
          </Space>
        }
      >
        {!batchId ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="请先选择批次" />
        ) : isLoading ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="加载中…" />
        ) : boardStore && (viewingAllStores || activeStore) ? (
          <SingleStoreBoard store={boardStore} />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={viewingAllStores ? '本批次暂无数据' : '门店不存在'}
          />
        )}
      </Card>

      <Card
        style={adminContentCardStyle}
        title={
          <Space>
            <TeamOutlined />
            <span>评价人明细</span>
            <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
              {viewingAllStores ? '全部门店' : activeStore?.storeLabel ?? ''}
            </Text>
          </Space>
        }
        extra={
          canPushRemind && batchId ? (
            <Popconfirm
              title={`向当前列表中 ${openInView.length} 名未完成评价人推送${imLabel}催办？`}
              description="仅推仍有待提交任务的人；已完成或未绑钉钉的会自动跳过。"
              okText="确认推送"
              cancelText="取消"
              disabled={openInView.length === 0 || remindMutation.isPending}
              onConfirm={remindOpenInView}
            >
              <Button
                type="primary"
                icon={<NotificationOutlined />}
                disabled={openInView.length === 0}
                loading={remindMutation.isPending && remindingId == null}
              >
                催办当前未完成（{openInView.length}）
              </Button>
            </Popconfirm>
          ) : null
        }
      >
        {!batchId ? (
          <Empty description="请先选择批次" />
        ) : loadingPeople ? (
          <Empty description="加载人员…" />
        ) : filteredPeople.length === 0 ? (
          <Empty description="没有符合筛选条件的评价人" />
        ) : (
          <Table<PersonRow>
            rowKey={(r) => String(r.evaluatorId ?? r.evaluateeId)}
            size="small"
            dataSource={filteredPeople}
            pagination={{ pageSize: 20, showTotal: (t) => `共 ${t} 人` }}
            columns={[
              {
                title: '评价人',
                key: 'name',
                width: 140,
                render: (_v, r) => (
                  <Space>
                    <span style={{ fontWeight: 600 }}>{r.name}</span>
                    {r.leftRoster ? <Tag color="default">已离职</Tag> : null}
                  </Space>
                ),
              },
              ...(viewingAllStores
                ? [
                    {
                      title: '门店',
                      key: 'store',
                      width: 140,
                      render: (_v: unknown, r: PersonRow) => (
                        <Space size={4}>
                          <span>{personStoreLabel(r.storeKey, r.unresolved)}</span>
                          {r.unresolved ? <Tag color="orange">未归店</Tag> : null}
                        </Space>
                      ),
                    },
                  ]
                : []),
              {
                title: '部门',
                key: 'dept',
                ellipsis: true,
                render: (_v, r) => (
                  <Text style={{ fontSize: 13, color: '#555' }}>{shortDept(r.deptPath)}</Text>
                ),
              },
              {
                title: '素质（我评的）',
                key: 'quality',
                width: 160,
                render: (_v, r) => (
                  <Space size={6}>
                    {qualityTag(r.quality.status)}
                    {r.quality.status !== 'N/A' ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {r.quality.done}/{r.quality.total}
                      </Text>
                    ) : null}
                  </Space>
                ),
              },
              {
                title: '胜任力+高潜（我评的）',
                key: 'comp',
                width: 180,
                render: (_v, r) => (
                  <Space size={6}>
                    {combinedTag(r.competencyHpCombined)}
                    {r.competency?.evaluable ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {r.competency.done}/{r.competency.total}
                      </Text>
                    ) : null}
                  </Space>
                ),
              },
              {
                title: '跳转',
                key: 'links',
                width: 160,
                render: () => (
                  <Space size={4}>
                    <Link to="/eval-tasks">评价进度</Link>
                    <span>·</span>
                    <Link to="/competency-hp/results">胜任力汇总</Link>
                  </Space>
                ),
              },
              ...(canPushRemind
                ? [
                    {
                      title: '催办',
                      key: 'remind',
                      width: 100,
                      render: (_v: unknown, r: PersonRow) => {
                        if (!isOpenPerson(r)) {
                          return <Text type="secondary">—</Text>;
                        }
                        return (
                          <Popconfirm
                            title={`向「${r.name}」推送${imLabel}催办？`}
                            okText="确认推送"
                            cancelText="取消"
                            onConfirm={() => remindOne(r.evaluatorId)}
                          >
                            <Button
                              type="link"
                              size="small"
                              icon={<NotificationOutlined />}
                              loading={remindMutation.isPending && remindingId === r.evaluatorId}
                              disabled={remindMutation.isPending}
                            >
                              催办
                            </Button>
                          </Popconfirm>
                        );
                      },
                    },
                  ]
                : []),
            ]}
          />
        )}
      </Card>
    </div>
  );
}
